import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { useClients } from '../../core/hooks/useClients';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { useAuth } from '../../core/contexts/AuthContext';
import { UserRole } from '../../core/models/User';
import { ClientStatus } from '../../core/models/Client';
import { findClientAccountForProject } from '../../core/utils/projectContact';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/DropdownMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { ClientForm } from './components/ClientForm';
import { ClientEditDialog } from './components/ClientEditDialog';
import { ClientDeleteDialog } from './components/ClientDeleteDialog';
import { ServiceAssignmentDialog } from './components/ServiceAssignmentDialog';

export const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizationId, currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const canManageProjects =
    currentUser?.role === UserRole.SUPER_ADMIN ||
    currentUser?.role === UserRole.CLIENT_ADMIN;
  // Super admins see all projects; others see only projects under their client account
  const effectiveOrgIdForClients = isSuperAdmin ? null : organizationId;
  const { clients, loading, error, createClient, updateClient, deleteClient } = useClients(effectiveOrgIdForClients);
  const { organizations } = useOrganizations();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<typeof clients[0] | null>(null);
  const [deletingClient, setDeletingClient] = useState<typeof clients[0] | null>(null);
  const [assigningServicesClient, setAssigningServicesClient] = useState<typeof clients[0] | null>(null);

  const getStatusColor = (status: ClientStatus) => {
    switch (status) {
      case ClientStatus.ACTIVE:
        return 'success';
      case ClientStatus.INACTIVE:
        return 'error';
      case ClientStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleCreateClient = async (data: any) => {
    try {
      const resolvedOrganizationId = data.organizationId || organizationId;
      if (!resolvedOrganizationId) {
        alert('Client ID is required. Please ensure you are logged in with a valid client context.');
        return;
      }
      await createClient({ ...data, organizationId: resolvedOrganizationId, clientId: resolvedOrganizationId });
      setIsCreateDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to create client:', err);
      let errorMessage = 'Failed to create project. Please try again.';
      if (err?.code === 'permission-denied') {
        errorMessage = 'You do not have permission to create projects.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = 'Operation failed. Please check that all required fields are filled.';
      } else if (err?.message) {
        errorMessage = err.message;
      }
      alert(errorMessage);
    }
  };

  const handleUpdateClient = async (data: any) => {
    if (!editingClient) return;
    try {
      await updateClient(editingClient.id, data);
      setEditingClient(null);
    } catch (err) {
      console.error('Failed to update client:', err);
    }
  };

  const handleDeleteClient = async () => {
    if (!deletingClient) return;
    try {
      await deleteClient(deletingClient.id);
      setDeletingClient(null);
    } catch (err) {
      console.error('Failed to delete client:', err);
      throw err;
    }
  };

  const handleAssignServices = async (serviceIds: string[], pricingOverrides: Record<string, number>) => {
    // TODO: Implement service assignment logic
    console.log('Assigning services:', serviceIds, pricingOverrides);
  };

  if (loading) {
    return <LoadingState message="Loading projects..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load projects" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        description="Manage your projects and their information"
        icon={Building2}
        actions={
          canManageProjects ? (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Project
            </Button>
          ) : undefined
        }
      />

      {clients.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No projects found"
          description="Get started by adding your first project"
          action={canManageProjects ? {
            label: 'Add Project',
            onClick: () => setIsCreateDialogOpen(true),
          } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <Card
              key={client.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{client.name}</CardTitle>
                    <Badge variant={getStatusColor(client.status)} className="mt-2">
                      {client.status}
                    </Badge>
                  </div>
                  {canManageProjects && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clients/${client.id}`);
                        }}
                      >
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingClient(client);
                        }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssigningServicesClient(client);
                        }}
                      >
                        Assign Services
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingClient(client);
                        }}
                        className="text-destructive"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {(() => {
                  const clientAccount = findClientAccountForProject(client, organizations);
                  return (
                    <div className="text-sm text-muted-foreground">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground/80">
                        Client
                      </span>
                      <p className="font-medium text-foreground truncate">
                        {clientAccount?.name || 'Unassigned'}
                      </p>
                      {clientAccount?.email && (
                        <p className="truncate text-xs mt-0.5">{clientAccount.email}</p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Client Dialog */}
      {/* Create Project Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <ClientForm
            onSubmit={handleCreateClient}
            onCancel={() => setIsCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      {canManageProjects && editingClient && (
        <ClientEditDialog
          open={!!editingClient}
          onClose={() => setEditingClient(null)}
          client={editingClient}
          onSubmit={handleUpdateClient}
        />
      )}

      {/* Delete Project Dialog */}
      {canManageProjects && deletingClient && (
        <ClientDeleteDialog
          open={!!deletingClient}
          onClose={() => setDeletingClient(null)}
          client={deletingClient}
          onConfirm={handleDeleteClient}
        />
      )}

      {/* Service Assignment Dialog */}
      {canManageProjects && assigningServicesClient && (
        <ServiceAssignmentDialog
          open={!!assigningServicesClient}
          onClose={() => setAssigningServicesClient(null)}
          client={assigningServicesClient}
          onAssign={handleAssignServices}
        />
      )}
    </div>
  );
};
